/**
 * Account Context
 * Manages global account state
 * Task 003 - Account Management
 */

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import {
  Account,
  CreateAccountData,
  UpdateAccountData,
  AccountContextType,
  AccountCloseResponse,
  AccountTransactionCount,
} from '../types/account';
import * as accountService from '../services/accountService';
import { useAuth } from './AuthContext';
import { usePlans } from './PlanContext';

const AccountContext = createContext<AccountContextType | undefined>(undefined);

interface AccountProviderProps {
  children: ReactNode;
}

export const AccountProvider: React.FC<AccountProviderProps> = ({ children }) => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [closedAccounts, setClosedAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { session, loading: authLoading } = useAuth();
  const { currentMonthPlan } = usePlans();

  // Calculate total pool from all accounts
  const totalPool = accounts.reduce((sum, account) => sum + account.balance, 0);

  const resetAccounts = useCallback(() => {
    setAccounts([]);
    setClosedAccounts([]);
    setError(null);
    setLoading(false);
  }, []);

  /**
   * Load all active user accounts
   */
  const loadAccounts = useCallback(async () => {
    if (!session) {
      resetAccounts();
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const userAccounts = await accountService.getUserAccounts('active');
      setAccounts(userAccounts);
    } catch (err) {
      if (err instanceof Error && err.message === 'Authentication token not available') {
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to load accounts');
      console.error('Error loading accounts:', err);
    } finally {
      setLoading(false);
    }
  }, [session, resetAccounts]);

  /**
   * Load closed user accounts
   */
  const loadClosedAccounts = useCallback(async () => {
    if (!session) {
      return;
    }

    try {
      const closed = await accountService.getUserAccounts('closed');
      setClosedAccounts(closed);
    } catch (err) {
      console.error('Error loading closed accounts:', err);
    }
  }, [session]);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!session) {
      resetAccounts();
      return;
    }

    loadAccounts();
  }, [authLoading, session?.user?.id, loadAccounts, resetAccounts]);

  /**
   * Create a new account
   */
  const createAccount = async (data: CreateAccountData): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      await accountService.createAccount(data);
      // Refresh accounts list
      await loadAccounts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create account');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Update an account
   */
  const updateAccount = async (accountId: string, data: UpdateAccountData): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      // Pass current month_plan_id if available (for creating adjustment transaction when balance changes)
      await accountService.updateAccount(accountId, data, currentMonthPlan?.id);
      // Refresh accounts list
      await loadAccounts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update account');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Delete an account
   */
  const deleteAccount = async (accountId: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      await accountService.deleteAccount(accountId);
      // Refresh accounts list
      await loadAccounts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete account');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Refresh active accounts list
   */
  const refreshAccounts = async (): Promise<void> => {
    await loadAccounts();
  };

  /**
   * Refresh closed accounts list
   */
  const refreshClosedAccounts = async (): Promise<void> => {
    await loadClosedAccounts();
  };

  /**
   * Close an account
   */
  const closeAccount = async (accountId: string, monthPlanId: string): Promise<AccountCloseResponse> => {
    try {
      setLoading(true);
      setError(null);
      const response = await accountService.closeAccount(accountId, monthPlanId);
      // Refresh both lists
      await loadAccounts();
      await loadClosedAccounts();
      return response;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to close account');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Reopen a closed account
   */
  const reopenAccount = async (accountId: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      await accountService.reopenAccount(accountId);
      // Refresh both lists
      await loadAccounts();
      await loadClosedAccounts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reopen account');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get transaction count for an account
   */
  const getTransactionCount = async (accountId: string): Promise<AccountTransactionCount> => {
    try {
      return await accountService.getTransactionCount(accountId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get transaction count');
      throw err;
    }
  };

  /**
   * Get account by ID from current state
   */
  const getAccountById = (accountId: string): Account | undefined => {
    return accounts.find((account) => account.id === accountId);
  };

  const value: AccountContextType = {
    accounts,
    closedAccounts,
    loading,
    error,
    totalPool,
    createAccount,
    updateAccount,
    deleteAccount,
    closeAccount,
    reopenAccount,
    getTransactionCount,
    refreshAccounts,
    refreshClosedAccounts,
    getAccountById,
  };

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
};

/**
 * Custom hook to use Account context
 */
export const useAccounts = (): AccountContextType => {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error('useAccounts must be used within an AccountProvider');
  }
  return context;
};
